import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { DateSerializer, UrlFormatter } from 'shared';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { GuestsV1ApiService } from 'apps/account/src/account-shared/services/guests-v1-api.service';
import { LayersApiService } from 'apps/account/src/account-shared/services/layers-api.service';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { PropertyApiService } from 'apps/account/src/account-shared/services/property-api.service';
import { V1HttpShimService } from 'apps/account/src/account-shared/services/v1-http-shim.service';
import { CooperativeLegacyState } from './cooperative-legacy.reducer';
import { selectCooperativeLegacyFeature } from './cooperative-legacy.reducer';

/**
 * `$ngRedux`-shaped facade over the native NgRx `cooperativeLegacyViews` feature
 * (mirrors AdvocacyNgReduxFacade). Lets the native cooperative components keep
 * their `getState`/`dispatch`/`subscribe` code verbatim while sourcing state from
 * NgRx instead of the AngularJS ng-redux store. `getState()` reshapes the flat
 * feature state back to `{ views: { cooperative }, apiData: { propertiesStatistics } }`.
 *
 * WS5 #10 Stage A inc 2h: the thunk extra-args resolve FULLY NATIVELY (the 2f
 * advocacy-facade recipe) — the facade no longer touches the AngularJS
 * `$injector`. The old 17-name list was a verbatim copy of the ng-redux store's
 * thunkExtras; the account-v2-wide census shows only $http/environment/
 * urlFormatter/Layers/PhotoContest/dateSerializer/Guests/SpacesService are EVER
 * destructured by any thunk (the cooperative bigPicture/partners thunks use
 * `{$http, environment}` / `{Layers, $http}` / `{$http, urlFormatter}`).
 */
@Injectable({ providedIn: 'root' })
export class CooperativeNgReduxFacade {
  private readonly httpShim = inject(V1HttpShimService);
  private readonly spacesService = inject(SpacesService);
  private readonly environment = inject(ENVIRONMENT_TOKEN) as Environment;
  private readonly layersApi = inject(LayersApiService);
  private readonly photoContestApi = inject(PhotoContestApiService);
  private readonly propertyApi = inject(PropertyApiService);
  private readonly guestsApi = inject(GuestsV1ApiService);
  private readonly extraArgs: Record<string, any> = {
    $http: this.httpShim,
    dateSerializer: (date: any) => DateSerializer.serialize(date),
    Layers: this.layersApi,
    urlFormatter: {
      format: (url: string, params?: any, appendSearch?: boolean) => UrlFormatter.format(url, params, appendSearch),
      combineUrlPath: (url: string, ...paths: string[]) => UrlFormatter.combineUrlPath(url, ...paths),
    },
    PhotoContest: this.photoContestApi,
    Property: this.propertyApi,
    SpacesService: this.spacesService,
    Guests: this.guestsApi,
    environment: this.environment,
  };
  private current: CooperativeLegacyState | undefined;

  constructor(private readonly store: Store) {
    this.store.select(selectCooperativeLegacyFeature).subscribe((s) => {
      if (s) this.current = s;
    });
  }

  getState = (): any => {
    const s: any = this.current || {};
    return {
      views: { cooperative: s.cooperative },
      apiData: { propertiesStatistics: s.propertiesStatistics },
    };
  };

  dispatch = (action: any): any =>
    typeof action === 'function'
      ? action(this.dispatch, this.getState, this.extraArgs)
      : this.store.dispatch(action);

  subscribe = (listener: () => void): (() => void) => {
    const sub = this.store.select(selectCooperativeLegacyFeature).subscribe(() => listener());
    return () => sub.unsubscribe();
  };
}
